import Phaser from "phaser";
import { BaseLevelScene, objProp, objFlippedH, collectiblePx } from "./BaseLevelScene";
import { baseValueForKind } from "../state/scoring";
import { levelById } from "../levels";
import { Prop } from "../tiles";
import { Tex, throwableTex, collectibleTex } from "../ui/textures";
import { Palette } from "../ui/palette";
import { registerPortraitAnims, type PortraitKey } from "../ui/portraits";
import { registerCharacterAnims, useCharacterSprite, updateCharacterAnim, getThrowMs, throwBallState, accessoryTransform } from "../ui/characterAnim";

const RUN_SPEED = 290; // scaled up with the bigger Dan / taller floors
const JUMP_SPEED = 430; // short hop (grab low floating items, never reach the next floor - that's what the elevators are for)

// On-screen size of the throwable comms items (phone / email / slack) as pickup,
// held ball, and thrown projectile. One knob so all three read at the same size.
const THROWABLE_PX = 30;

// Dan's on-screen size. His atlas frames are 50x64 and he was drawn at scale 1
// (which felt small); this single knob bumps the art AND the hitbox together.
// Worn accessories and the held/thrown ball read this too, so their offsets and
// sizes scale with Dan instead of drifting out of place. Tune freely.
// Dan's on-screen size factor (world-space): throw/accessory offsets and the PE guy
// all multiply by this, so one knob sizes everything together. The art is now packed
// at 3x (CONTENT_H 192), so the sprite itself renders at DAN_SCALE/DAN_ART (crisp,
// near 1:1) while offsets keep using the full DAN_SCALE.
const DAN_SCALE = 3.0;
const DAN_ART = 3; // atlas pack multiple vs the original 64px (see pack-character.mjs)

// Collectible upgrades: worn texture + pickup icon for each accessory key. Keys
// match the accessory names in dan-accessories.json (see characterAnim.ts).
const UPGRADES = [
  { key: "headphones", worn: "acc-headphones", pickup: "pickup-headphones" },
  { key: "watch", worn: "acc-watch", pickup: "pickup-watch" },
];

// Random PE Guy (Level 1 payoff NPC). Scale is a single knob: he's a 128px
// portrait, so ~0.55 lands him near Dan's height. Easy to retune.
const PE_SCALE = 0.5;
const PE_ANIMS: PortraitKey[] = ["pe-idle", "pe-talk", "pe-yell", "pe-paper-show", "pe-paper-hide", "pe-money", "pe-raise-eyebrow"];
/** How often the idle PE Guy plays his idle clip while Dan is not interacting with him. */
const PE_IDLE_EVERY_MS = 5000;

/** Interstitial dialogue-box geometry. The panel is sized to each line's text height
 *  (starting at a single line, growing only as needed) with its bottom anchored near the
 *  screen bottom. The portrait floats above the panel; the name tab sits at the panel top. */
const PE_DLG_PAD = 18;
const PE_DLG_MARGIN = 40; // left/right/bottom screen margin
const PE_DLG_CROP = { x: 16, y: 0, w: 96, h: 110 }; // head-to-legs sub-rect of the 128 frame
const PE_DLG_ZOOM = 2.25;

/** One line of the first-meeting interstitial between the PE Guy and Dan. `who` picks
 *  the name plate + which portrait shows; `anim` is the PE Guy portrait clip for his
 *  lines; `hold`/`auto` make a beat linger then advance itself (the eyebrow-raise beat). */
type PeBeat = { who: "pe" | "dan"; text: string; anim?: PortraitKey; holdMs?: number; auto?: boolean; loop?: boolean };

// The PE Guy's money animation sets a bag down at his feet; we hand that drawn
// bag off to a real, collectable one at the exact frame + spot it lands. Measured
// off the 256px source art (bag center in the drop-frame cell); PE_DROP_FRAME is
// the 1-based money-anim frame where the drawn bag reaches the ground (it's gone
// from the art the very next frame, so the real one takes its place seamlessly).
const PE_DROP_FRAME = 27;
const PE_BAG_CELL = { x: 83, y: 217 }; // bag center in the 256px money-drop cell

interface Elevator {
  sprite: Phaser.Physics.Arcade.Image;
  topY: number;
  bottomY: number;
  speed: number;
  rising: boolean; // latched true once Dan steps on; then it climbs to the top
  done: boolean; // reached the top and parked (never comes back down)
}

/** Dan - grind/accumulation across long horizontal floors. Run back and forth,
 *  ride elevators up between floors, and throw comms items (they fly far now)
 *  to snag the hard-to-reach collectibles. Camera follows in x and y. */
export class Level1DanScene extends BaseLevelScene {
  private tier = 0; // 0 none, 2 phone, 3 email, 4 slack
  private facing = 1;
  private throwUntil = 0; // time.now window during which the throw pose is held
  private projectiles!: Phaser.Physics.Arcade.Group;
  private elevators: Elevator[] = [];
  private heldBall?: Phaser.GameObjects.Image; // the ball in Dan's hand during a throw
  private thrownFor = -1; // throwUntil value already launched, so each throw fires once
  private equipped = new Set<string>(); // upgrades Dan has collected and now wears
  private accSprites: Record<string, Phaser.GameObjects.Image> = {}; // worn accessory sprites by key
  private peGuy?: Phaser.GameObjects.Sprite; // Random PE Guy waiting at the top-floor entrance
  private metPeGuy = false; // payoff beat fires once
  private peGuy3f?: Phaser.GameObjects.Sprite; // PE Guy on the 3rd floor
  private metPeGuy3f = false;
  // First-meeting interstitial (a non-fullscreen dialogue box between the PE Guy and Dan).
  private peTalking = false; // true while the interstitial is open (freezes Dan)
  private peDlgObjs: Phaser.GameObjects.GameObject[] = []; // torn down on close
  private peDlgPanel?: Phaser.GameObjects.Rectangle; // the (dynamically sized) text panel
  private peDlgWinFrame?: Phaser.GameObjects.Rectangle; // dark frame behind the portrait
  private peDlgPortrait?: Phaser.GameObjects.Sprite; // the PE Guy portrait, cropped waist-up
  private peDlgDan?: Phaser.GameObjects.Image; // Dan's static portrait, for his lines
  private peDlgPlate?: Phaser.GameObjects.Rectangle; // name tab under the portrait
  private peDlgPlateLabel?: Phaser.GameObjects.Text;
  private peNameX = 0; // portrait centre x (name tab is centred here, at the panel top)
  private peNameY = 0;
  private peDlgBody?: Phaser.GameObjects.Text;
  private peDlgPrompt?: Phaser.GameObjects.Text;
  private peTyper?: Phaser.Time.TimerEvent;
  private peBeats: PeBeat[] = [];
  private peBeatIdx = 0;
  private peTypeFull = ""; // the current beat's full text
  private peTyping = false;
  // Money bags the PE Guy drops. Authored as `money` collectibles on the top floor,
  // but held back until Dan reaches the PE trigger - then they rain down and become
  // collectable (the "here's the money" payoff). Non-money collectibles spawn normally.
  private peDropSpecs: { x: number; y: number; value: number }[] = [];

  constructor() {
    super("Level1Dan");
  }

  create() {
    this.elevators = [];
    this.tier = 0;
    this.metPeGuy = false;
    this.metPeGuy3f = false;
    this.peTalking = false;
    this.peDropSpecs = [];
    this.buildWorld(levelById("dan"));
    // Invisible floors: Dan runs directly on the painted floors of the office-mid
    // backdrop. Hiding the layer keeps collision fully intact (visibility and Arcade
    // tile collision are independent in Phaser), so the floors still hold him up.
    this.world.setVisible(false);
    this.projectiles = this.physics.add.group();

    const spawn = this.findSpawn();
    this.createPlayer(spawn.x, spawn.y);
    // Animated character (art-pipeline spike): swap the placeholder rect onto the
    // loaded atlas + drive it from a small idle/run/jump/throw state machine.
    registerCharacterAnims(this);
    useCharacterSprite(this.player);
    this.player.setScale(DAN_SCALE / DAN_ART); // art is packed 3x, so render near 1:1 (crisp)
    this.player.setDepth(10); // so held ball / accessories can sit just in front of / behind Dan
    for (const u of UPGRADES) this.accSprites[u.key] = this.add.image(0, 0, u.worn).setVisible(false);
    // Attachments follow the player, which Arcade Physics moves in its POST_UPDATE
    // step (after update()). Position them there too, or they lag a frame behind
    // Dan during fast vertical motion (jumps).
    this.events.on(Phaser.Scenes.Events.POST_UPDATE, this.lateFollow, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.events.off(Phaser.Scenes.Events.POST_UPDATE, this.lateFollow, this),
    );

    let sawUpgrade = false;
    let sawPeGuy = false;
    let exitFallback: { x: number; y: number } | undefined;
    this.forEachObject((obj, cls) => {
      switch (cls) {
        case "collectible":
          // Money bags are held back for the PE Guy to drop (see triggerPeGuy);
          // everything else (laptop / vw / document) spawns as normal.
          if (objProp(obj, Prop.KIND, "money") === "money") {
            this.peDropSpecs.push({ x: obj.x!, y: obj.y!, value: objProp(obj, Prop.VALUE, baseValueForKind("money")) });
          } else {
            this.spawnCollectible(obj);
          }
          break;
        case "pe_trigger": {
          const zone = this.add.zone(obj.x!, obj.y!, obj.width!, obj.height!).setOrigin(0, 0);
          this.physics.add.existing(zone, true);
          this.physics.add.overlap(this.player, zone, () => this.triggerPeGuy());
          break;
        }
        case "sheepy":
          this.spawnSheepy(obj);
          break;
        case "elevator":
          this.makeElevator(obj.x!, obj.y!, objProp(obj, Prop.RISE, 8), objProp(obj, Prop.SPEED, 70));
          break;
        case "upgrade":
          this.spawnUpgrade(obj.x!, obj.y!, objProp(obj, Prop.ACC, "headphones"));
          sawUpgrade = true;
          break;
        case "pe_guy":
          // Tiled tile object: bottom-left origin, so obj.y is the feet baseline.
          // Facing is authored by flipping the placeholder horizontally in Tiled.
          this.spawnPeGuyAt(obj.x! + (obj.width ?? 0) / 2, obj.y!, objFlippedH(obj));
          sawPeGuy = true;
          break;
        case "pe_guy_3f":
          this.spawnPeGuy3fAt(obj.x! + (obj.width ?? 0) / 2, obj.y!, objFlippedH(obj));
          break;
        case "pe_trigger_3f": {
          const z3f = this.add.zone(obj.x!, obj.y!, obj.width!, obj.height!).setOrigin(0, 0);
          this.physics.add.existing(z3f, true);
          this.physics.add.overlap(this.player, z3f, () => this.triggerPeGuy3f());
          break;
        }
        case "throwable_pickup": {
          const tier = objProp(obj, Prop.TIER, 2);
          const pickup = this.physics.add.sprite(obj.x!, obj.y!, throwableTex(tier));
          pickup.setScale(THROWABLE_PX / Math.max(pickup.width, pickup.height));
          (pickup.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
          this.tweens.add({ targets: pickup, y: obj.y! - 6, duration: 800, yoyo: true, repeat: -1 });
          this.physics.add.overlap(this.player, pickup, () => {
            if (!pickup.active) return;
            pickup.destroy();
            this.tier = Math.max(this.tier, tier);
            this.flashText(`Throwable → ${this.tierName()}`, Palette.dan);
            this.updateInfo();
          });
          break;
        }
        case "exit": {
          const zone = this.add.zone(obj.x!, obj.y!, obj.width!, obj.height!).setOrigin(0, 0);
          this.physics.add.existing(zone, true);
          this.physics.add.overlap(this.player, zone, () => this.reachExit());
          // Fallback PE spawn (feet at the zone's bottom-center) if the map has no
          // `pe_guy` object placed. A placed pe_guy object overrides this.
          exitFallback = { x: obj.x! + (obj.width ?? 0) / 2, y: obj.y! + (obj.height ?? 0) };
          break;
        }
      }
    });
    if (!sawPeGuy && exitFallback) this.spawnPeGuyAt(exitFallback.x, exitFallback.y);
    // If the map didn't author any `upgrade` objects, fall back to dropping them
    // near Dan's spawn (original behavior), so existing maps keep working.
    if (!sawUpgrade) this.spawnUpgradePickups(spawn);

    // Thrown items pop when they hit geometry (no timed despawn - they fly far).
    this.physics.add.collider(this.projectiles, this.world, (p) =>
      this.resolveThrow(p as Phaser.GameObjects.GameObject),
    );
    this.physics.add.overlap(this.projectiles, this.collectibles, (p, c) =>
      this.collectSprite(c as Phaser.Physics.Arcade.Sprite, p as Phaser.GameObjects.GameObject),
    );

    this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);

    this.buildHud();
    this.buildInput();
    this.updateInfo();

    // Start the elapsed clock for the speed bonus (applied in reachExit).
    this.levelStartMs = this.time.now;
  }

  private makeElevator(x: number, y: number, riseTiles: number, speed: number) {
    const plat = this.physics.add.image(x, y, Tex.elevator);
    plat.setImmovable(true);
    const pb = plat.body as Phaser.Physics.Arcade.Body;
    pb.setAllowGravity(false);
    pb.setOffset(0, 2); // drop the collision surface 2px so Dan plants on the deck, not floating above it
    this.physics.add.collider(this.player, plat);
    this.elevators.push({ sprite: plat, bottomY: y, topY: y - riseTiles * 32, speed, rising: false, done: false });
  }

  private tierName(): string {
    return { 0: "-", 2: "phone", 3: "email", 4: "slack" }[this.tier] ?? "-";
  }

  private updateInfo() {
    const hint = this.levelHint || "Collect items to earn ★";
    this.setHudInfo(`Throwable: ${this.tierName()}\n${hint}`);
  }

  /** Place the Random PE Guy with his feet at (x, y), facing left toward the
   *  incoming Dan. His idle plays only once every 30s (he rests on frame 0 in
   *  between); he switches to the money-offer pose when Dan reaches the PE
   *  trigger (triggerPeGuy). Authored via a `pe_guy` tile object in Tiled. */
  private spawnPeGuyAt(x: number, y: number, flipX = false) {
    registerPortraitAnims(this, PE_ANIMS);
    this.peGuy = this.add
      .sprite(x, y + 2, "pe-idle") // nudge down 2px so his feet sit on the floor
      .setOrigin(0.5, 1)
      .setScale(PE_SCALE * DAN_SCALE) // keep him near Dan's height as Dan scales
      .setDepth(4) // behind Dan (depth 10) so he stands in front when they meet
      .setFlipX(flipX) // facing authored in Tiled (flip the placeholder); default art faces left
      .setFrame(0); // rest on the first frame; the idle plays on a 30s cadence
    this.time.addEvent({
      delay: PE_IDLE_EVERY_MS,
      loop: true,
      callback: () => {
        // Idle clip while we're not interacting with him (roughly every 5s).
        if (this.peGuy && !this.metPeGuy && !this.peTalking) this.peGuy.play({ key: "pe-idle", repeat: 0 });
      },
    });
  }

  /** PE payoff beat, fired by the `pe_trigger` area at the top-floor entrance
   *  (not the exit). The PE Guy plays his full money-offer animation and, part
   *  way through, rains down the money bags for Dan to collect on the way to the
   *  exit. Runs once. */
  private triggerPeGuy() {
    if (this.metPeGuy) return;
    this.metPeGuy = true;
    // First the interstitial (PE Guy pitches, asks Dan's rate, raises an eyebrow), then
    // the money animation plays as before once the dialogue closes.
    this.openPeDialogue(
      [
        { who: "pe", text: "This is the best piece of DD I've seen in years.", anim: "pe-paper-hide", loop: false },
        { who: "pe", text: "So let's talk about your fee. What's this going to cost me?", anim: "pe-paper-show" },
        { who: "dan", text: "25K?" },
        { who: "pe", text: "...", anim: "pe-raise-eyebrow", loop: false, holdMs: 2000, auto: true },
        { who: "pe", text: "You should be charging more! The price is 50K!", anim: "pe-yell", loop: false },
      ],
      () => this.playPeMoney(),
    );
  }

  private spawnPeGuy3fAt(x: number, y: number, flipX = false) {
    registerPortraitAnims(this, PE_ANIMS);
    this.peGuy3f = this.add
      .sprite(x, y + 2, "pe-idle")
      .setOrigin(0.5, 1)
      .setScale(PE_SCALE * DAN_SCALE)
      .setDepth(4)
      .setFlipX(flipX)
      .setFrame(0);
    this.time.addEvent({
      delay: PE_IDLE_EVERY_MS,
      loop: true,
      callback: () => {
        if (this.peGuy3f && !this.metPeGuy3f && !this.peTalking) this.peGuy3f.play({ key: "pe-idle", repeat: 0 });
      },
    });
  }

  private triggerPeGuy3f() {
    if (this.metPeGuy3f) return;
    this.metPeGuy3f = true;
    this.openPeDialogue(
      [
        { who: "pe", text: "Hey. I'm some random private equity guy.", anim: "pe-talk" },
        { who: "pe", text: "You're selling yourself short doing this agency work. You should do this tech due dilligence project for me instead.", anim: "pe-talk" },
        { who: "dan", text: "Hello Random PE Guy. We can definitely do that 100%." },
        { who: "pe", text: "Ok. Is it done yet?! Get to work!", anim: "pe-yell", loop: false },
      ],
      () => {
        this.collectibles.getChildren().forEach((c) => {
          if ((c as Phaser.GameObjects.Sprite).getData("kind") === "vw") {
            (c as Phaser.GameObjects.Sprite).setData("value", -30);
          }
        });

        const W = this.scale.width;
        const flash = this.add
          .rectangle(0, 0, W, 40, 0xff0000, 0.6)
          .setOrigin(0, 0)
          .setScrollFactor(0)
          .setDepth(1001);
        this.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 800,
          ease: "Sine.easeOut",
          onComplete: () => flash.destroy(),
        });

        this.flashText("Stay away from VW logos!", "#ff4444");
      },
    );
  }

  protected override collectSprite(
    c: Phaser.Physics.Arcade.Sprite,
    projectile?: Phaser.GameObjects.GameObject,
  ) {
    const val = (c.getData("value") as number | undefined) ?? 0;
    const kind = (c.getData("kind") as string) ?? "";
    super.collectSprite(c, projectile);
    if (kind === "vw" && val < 0) {
      const W = this.scale.width;
      const flash = this.add
        .rectangle(0, 0, W, 40, 0xff0000, 0.6)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(1001);
      this.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 800,
        ease: "Sine.easeOut",
        onComplete: () => flash.destroy(),
      });
      this.flashText("Stay away from VW logos!", "#ff4444");
    }
  }

  /** The money payoff: the PE Guy plays his money-offer animation and, part way
   *  through, rains down the bags. Unchanged from before; now runs after the
   *  interstitial closes. */
  private playPeMoney() {
    const g = this.peGuy;
    g?.play("pe-money");
    this.convertCollectiblesToMoney(); // every remaining collectible becomes a money bag
    if (!g) {
      this.dropPeMoney();
      return;
    }
    // Spawn the money the instant the animation sets its bag down (frame 27), so
    // the real bag replaces the drawn one at his feet with no gap.
    const onFrame = (_a: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
      if (frame.index < PE_DROP_FRAME) return;
      g.off(Phaser.Animations.Events.ANIMATION_UPDATE, onFrame);
      this.spawnPeFeetBag();
      this.dropPeMoney(); // the authored top-floor bags
    };
    g.on(Phaser.Animations.Events.ANIMATION_UPDATE, onFrame);
  }

  // ---- first-meeting interstitial (non-fullscreen dialogue box) ------------

  /** Open the PE Guy / Dan dialogue box: a panel pinned to the bottom of the screen
   *  (it does NOT take over the whole view) with the speaker's name, their line typed
   *  out, and a portrait. The PE Guy portrait is zoomed and cropped to roughly waist-up
   *  and plays his talk / raise-eyebrow clips. Advances on click / space; the final
   *  auto beat holds on its last frame, then closes and runs `onClose`. */
  private openPeDialogue(beats: PeBeat[], onClose: () => void) {
    this.peTalking = true;
    this.player.setVelocity(0, 0);
    const W = this.scale.width;
    const D = 2000; // depth: above gameplay, below nothing else in-level
    const px = PE_DLG_MARGIN;
    const pw = W - PE_DLG_MARGIN * 2;

    // The panel: a bottom band whose HEIGHT is set per beat to fit that line (starts at a
    // single line, grows only when a line wraps). layoutPeDialogue positions it and the
    // portrait/name tab/text/prompt; here we just create the objects and their styles.
    const panel = this.add.rectangle(px, 0, pw, 40, 0x0a0d14, 0.92).setOrigin(0, 0).setScrollFactor(0).setDepth(D).setStrokeStyle(2, 0x2b3345);

    // Portrait window: floats ABOVE the panel (its bottom rests on the panel top, like the
    // cutscenes) so the name tab sits under it at the panel top. The PE Guy is a 128px
    // full-body frame cropped to roughly waist-up with setCrop (screen-pinned, no mask).
    const winW = Math.round(PE_DLG_CROP.w * PE_DLG_ZOOM);
    const winH = Math.round(PE_DLG_CROP.h * PE_DLG_ZOOM);
    const winFrame = this.add.rectangle(0, 0, winW, winH, 0x05070c, 0.9).setOrigin(0, 0).setScrollFactor(0).setDepth(D + 1).setStrokeStyle(2, 0x2b3345);

    // Origin top-centre + a centred crop means setFlipX mirrors him in place, so he faces
    // RIGHT toward Dan.
    const portrait = this.add
      .sprite(0, 0, "pe-idle", 0)
      .setOrigin(0.5, 0)
      .setScale(PE_DLG_ZOOM)
      .setFlipX(true)
      .setScrollFactor(0)
      .setDepth(D + 2)
      .setVisible(false);
    portrait.setCrop(PE_DLG_CROP.x, PE_DLG_CROP.y, PE_DLG_CROP.w, PE_DLG_CROP.h);

    const dan = this.add.image(0, 0, "cutscene-dan").setScrollFactor(0).setDepth(D + 2).setVisible(false);
    if (this.textures.exists("cutscene-dan")) {
      const t = this.textures.get("cutscene-dan").getSourceImage() as { width: number; height: number };
      dan.setScale(Math.min((winW - 8) / t.width, (winH - 8) / t.height));
    }

    const body = this.add
      .text(0, 0, "", {
        fontFamily: "monospace",
        fontSize: "17px",
        color: "#e9edf5",
        lineSpacing: 4,
        wordWrap: { width: pw - PE_DLG_PAD * 2 },
      })
      .setScrollFactor(0)
      .setDepth(D + 2);
    const prompt = this.add
      .text(0, 0, "▸", { fontFamily: "monospace", fontSize: "18px", color: "#8a93a6" })
      .setOrigin(1, 1)
      .setScrollFactor(0)
      .setDepth(D + 2)
      .setVisible(false);
    this.tweens.add({ targets: prompt, alpha: 0.25, duration: 500, yoyo: true, repeat: -1 });

    this.peDlgObjs = [panel, winFrame, portrait, dan, body, prompt];
    this.peDlgPanel = panel;
    this.peDlgWinFrame = winFrame;
    this.peDlgPortrait = portrait;
    this.peDlgDan = dan;
    this.peDlgBody = body;
    this.peDlgPrompt = prompt;
    this.peBeats = beats;
    this.peOnClose = onClose;

    this.input.on("pointerdown", this.advancePeDialogue, this);
    this.input.keyboard?.on("keydown-SPACE", this.advancePeDialogue, this);
    this.input.keyboard?.on("keydown-ENTER", this.advancePeDialogue, this);

    this.showPeBeat(0);
  }

  /** Size the panel to fit `full` (min one line, grows for wrapped lines), anchor its
   *  bottom near the screen bottom, and reposition the portrait, name-tab anchor, text,
   *  and prompt. Called per beat before typing so the box never resizes mid-line. */
  private layoutPeDialogue(full: string) {
    const body = this.peDlgBody;
    const panel = this.peDlgPanel;
    if (!body || !panel) return;
    const W = this.scale.width;
    const H = this.scale.height;
    const px = PE_DLG_MARGIN;
    const pw = W - PE_DLG_MARGIN * 2;
    const PAD = PE_DLG_PAD;

    // Measure a single line and the full (wrapped) text height.
    body.setText("Xg");
    const lineH = body.height;
    body.setText(full);
    const textH = body.height;
    body.setText("");
    const contentH = Math.max(lineH, Math.min(textH, lineH * 6)); // cap runaway growth
    const panelH = Math.round(contentH + PAD * 2);

    const bottom = H - PE_DLG_MARGIN;
    const py = bottom - panelH;
    panel.setPosition(px, py).setSize(pw, panelH);

    const winW = Math.round(PE_DLG_CROP.w * PE_DLG_ZOOM);
    const winH = Math.round(PE_DLG_CROP.h * PE_DLG_ZOOM);
    const winX = px - 2;
    const cx = winX + winW / 2;
    const winY = py - winH; // portrait bottom rests on the panel top
    this.peDlgWinFrame?.setPosition(winX, winY);
    this.peDlgPortrait?.setPosition(cx, winY - PE_DLG_CROP.y * PE_DLG_ZOOM);
    this.peDlgDan?.setPosition(cx, winY + winH / 2);
    this.peNameX = cx;
    this.peNameY = py;
    body.setPosition(px + PAD, py + PAD);
    this.peDlgPrompt?.setPosition(px + pw - 12, py + panelH - 8);
  }

  private peOnClose?: () => void;

  private showPeBeat(i: number) {
    this.peBeatIdx = i;
    const beat = this.peBeats[i];
    this.layoutPeDialogue(beat.text); // size the box to this line before typing
    const isPe = beat.who === "pe";
    this.setPeNamePlate(isPe ? "P.E. Guy" : "Dan", isPe ? 0xe2b34d : Palette.dan);
    this.peDlgPrompt?.setVisible(false);

    // Portrait: PE Guy animates (talk / raise-eyebrow); Dan shows his static portrait.
    this.peDlgPortrait?.setVisible(isPe);
    this.peDlgDan?.setVisible(!isPe);
    if (isPe && this.peDlgPortrait) {
      const repeat = (beat.loop ?? true) ? -1 : 0;
      this.peDlgPortrait.play({ key: beat.anim ?? "pe-talk", repeat });
    }

    this.startPeType(beat.text, () => {
      if (isPe && this.peDlgPortrait && (beat.loop ?? true)) {
        this.peDlgPortrait.stop();
      }
      if (beat.auto) {
        this.time.delayedCall(beat.holdMs ?? 1500, () => this.advancePeDialogue());
      } else {
        this.peDlgPrompt?.setVisible(true);
      }
    });
  }

  /** Build (or rebuild) the speaker's name tab: a coloured tab with dark uppercase text,
   *  centred under the portrait at the panel's top edge - matching the cutscene name plates. */
  private setPeNamePlate(name: string, color: number) {
    this.peDlgPlate?.destroy();
    this.peDlgPlateLabel?.destroy();
    const D = 2000;
    const label = this.add
      .text(this.peNameX, this.peNameY, name.toUpperCase(), {
        fontFamily: "monospace",
        fontSize: "15px",
        fontStyle: "bold",
        color: "#0b0d12",
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(D + 4);
    const plate = this.add
      .rectangle(this.peNameX, this.peNameY, label.width + 24, 24, color, 1)
      .setScrollFactor(0)
      .setDepth(D + 3)
      .setStrokeStyle(2, 0x0b0d12);
    this.peDlgPlate = plate;
    this.peDlgPlateLabel = label;
  }

  /** Type the beat text out one character at a time; `onDone` fires when complete. */
  private startPeType(full: string, onDone: () => void) {
    this.peTyper?.remove();
    this.peTypeFull = full;
    this.peTyping = true;
    this.peDlgBody?.setText("");
    let n = 0;
    this.peTyper = this.time.addEvent({
      delay: 26,
      repeat: full.length - 1,
      callback: () => {
        n++;
        this.peDlgBody?.setText(full.slice(0, n));
        if (n >= full.length) {
          this.peTyping = false;
          onDone();
        }
      },
    });
    this.peTypeDone = onDone;
  }
  private peTypeDone?: () => void;

  /** Click / space: finish the current type-out if still typing, else next beat (or close). */
  private advancePeDialogue() {
    if (!this.peTalking) return;
    if (this.peTyping) {
      this.peTyper?.remove();
      this.peTyping = false;
      this.peDlgBody?.setText(this.peTypeFull);
      this.peTypeDone?.();
      return;
    }
    const next = this.peBeatIdx + 1;
    if (next < this.peBeats.length) this.showPeBeat(next);
    else this.closePeDialogue();
  }

  private closePeDialogue() {
    if (!this.peTalking) return;
    this.peTalking = false;
    this.peTyper?.remove();
    this.input.off("pointerdown", this.advancePeDialogue, this);
    this.input.keyboard?.off("keydown-SPACE", this.advancePeDialogue, this);
    this.input.keyboard?.off("keydown-ENTER", this.advancePeDialogue, this);
    this.peDlgObjs.forEach((o) => o.destroy());
    this.peDlgObjs = [];
    this.peDlgPlate?.destroy();
    this.peDlgPlateLabel?.destroy();
    this.peDlgPlate = undefined;
    this.peDlgPlateLabel = undefined;
    this.peDlgPanel = undefined;
    this.peDlgWinFrame = undefined;
    this.peDlgPortrait = undefined;
    this.peDlgDan = undefined;
    const cb = this.peOnClose;
    this.peOnClose = undefined;
    cb?.();
  }

  /** Reaching the PE Guy flips every collectible still on the board into a money bag
   *  (his "take the money" pitch made literal). Already-collected ones are gone from the
   *  group; the overlap handlers read kind/value at pickup, so re-tagging is enough. */
  private convertCollectiblesToMoney() {
    for (const child of this.collectibles.getChildren()) {
      const c = child as Phaser.Physics.Arcade.Sprite;
      if (!c.active || c.getData("kind") === "money") continue;
      c.setTexture(collectibleTex("money"));
      c.setScale(collectiblePx("money") / Math.max(c.width, c.height));
      c.setData("kind", "money");
      c.setData("value", baseValueForKind("money"));
      const base = c.scaleX;
      this.tweens.add({ targets: c, scaleX: base * 1.25, scaleY: base * 1.25, duration: 130, yoyo: true }); // pop on transform
    }
  }

  /** World position where the PE Guy's drawn bag lands, from the source-art
   *  measurement: the 256px cell maps to a 128px frame (÷2) drawn at PE_SCALE *
   *  DAN_SCALE with a bottom-center origin, and flips with his facing. */
  private peBagDropPos() {
    const g = this.peGuy!;
    const s = PE_SCALE * DAN_SCALE;
    const dx = ((PE_BAG_CELL.x - 128) / 2) * s; // cell center x = 128
    const dy = ((PE_BAG_CELL.y - 256) / 2) * s; // cell bottom (feet anchor) = 256
    return { x: g.x + (g.flipX ? -dx : dx), y: g.y + dy };
  }

  /** The real, collectable bag the PE Guy sets down at his feet, lined up with the
   *  drawn one and pickable on walk-over (and by throws, via the group overlap). */
  private spawnPeFeetBag() {
    const pos = this.peBagDropPos();
    // Nudge down so the bag sits on the surface, matching spawnCollectible / dropPeMoney.
    const c = this.collectibles.create(pos.x, pos.y + 4, collectibleTex("money")) as Phaser.Physics.Arcade.Sprite;
    c.setScale(collectiblePx("money") / Math.max(c.width, c.height));
    c.setData("kind", "money");
    c.setData("value", baseValueForKind("money"));
    this.totalCollectibles++;
    this.physics.add.overlap(this.player, c, () => this.collectSprite(c));
    this.refreshHud();
  }

  /** Rain the held-back money bags onto the top floor at their authored spots,
   *  each with a short bounce-in, and wire up collection (walk-over + throws). */
  private dropPeMoney() {
    for (const spec of this.peDropSpecs) {
      const c = this.collectibles.create(spec.x, spec.y - 90, collectibleTex("money")) as Phaser.Physics.Arcade.Sprite;
      c.setScale(collectiblePx("money") / Math.max(c.width, c.height));
      c.setData("kind", "money");
      c.setData("value", spec.value);
      this.totalCollectibles++;
      // The group-wide projectile overlap covers members added here; add the
      // per-sprite walk-over overlap to match spawnCollectible.
      this.physics.add.overlap(this.player, c, () => this.collectSprite(c));
      this.tweens.add({
        targets: c,
        y: spec.y + 2, // sit on the surface, matching spawnCollectible's money nudge
        duration: 520,
        ease: "Bounce.easeOut",
        delay: Phaser.Math.Between(0, 260),
      });
    }
    this.refreshHud();
  }

  private reachExit() {
    if (this.finished) return;
    const pct = this.totalCollectibles ? this.collectedCount / this.totalCollectibles : 0;
    const stars = pct >= 0.8 ? 3 : pct >= 0.5 ? 2 : 1;
    this.applySpeedBonus("dan", this.elapsedSeconds());
    this.completeLevel(stars, [
      "Nice job! You've evolved from agency work to tech due dilligence.",
      `Collected ${this.collectedCount}/${this.totalCollectibles} (${Math.round(pct * 100)}%).`,
    ]);
  }

  /** Begin a throw: latch the pose window sized to the running/standing variant.
   *  The ball is held in-hand and actually launches at the release frame (see
   *  updateThrowBall), so the projectile leaves Dan's hand, not his center. */
  private startThrow() {
    if (this.tier <= 0) return;
    const running = Math.abs((this.player.body as Phaser.Physics.Arcade.Body).velocity.x) > 5;
    this.throwUntil = this.time.now + getThrowMs(running);
  }

  /** Carry the ball through the throw: hold it at the per-frame hand position
   *  (in front of / behind Dan per the throw.html alignment), then launch a
   *  flying projectile once, from the last in-hand spot, at the release frame. */
  private updateThrowBall() {
    const st = this.time.now < this.throwUntil ? throwBallState(this.player) : null;
    if (!st) {
      this.heldBall?.setVisible(false);
      return;
    }
    const tex = throwableTex(this.tier);
    if (!st.released) {
      const x = this.player.x + this.facing * st.ox * DAN_SCALE;
      const y = this.player.y + st.oy * DAN_SCALE;
      if (!this.heldBall) this.heldBall = this.add.image(x, y, tex);
      else this.heldBall.setTexture(tex);
      this.heldBall
        .setScale(THROWABLE_PX / Math.max(this.heldBall.width, this.heldBall.height))
        .setPosition(x, y)
        .setVisible(true)
        .setDepth(this.player.depth + (st.front ? 1 : -1));
      return;
    }
    this.heldBall?.setVisible(false);
    if (this.thrownFor !== this.throwUntil) {
      this.thrownFor = this.throwUntil; // one projectile per throw
      const p = this.projectiles.create(
        this.player.x + this.facing * st.launchOx * DAN_SCALE,
        this.player.y + st.launchOy * DAN_SCALE,
        tex,
      ) as Phaser.Physics.Arcade.Sprite;
      p.setScale(THROWABLE_PX / Math.max(p.width, p.height)); // Arcade body scales with the sprite
      p.setVelocity(this.facing * (620 + this.tier * 50), -300); // fly much farther; higher tiers faster still
      p.setAngularVelocity(this.facing * 540); // spin in the throw direction (visual only)
      // Safety despawn: a throw that never hits geometry still resolves its combo
      // batch (and scores) instead of flying forever.
      this.time.delayedCall(2500, () => {
        if (p.active) this.resolveThrow(p as Phaser.GameObjects.GameObject);
      });
    }
  }

  /** Runs after physics has moved the player sprite, so attachments match Dan's
   *  final position this frame instead of trailing a frame behind during jumps. */
  private lateFollow() {
    if (this.finished) return;
    this.updateThrowBall();
    this.updateAccessories();
  }

  /** Spawn one upgrade pickup at (x, y); walking into it equips that accessory
   *  for the rest of the run. Used by both the Tiled `upgrade` objects and the
   *  near-spawn fallback. Unknown `acc` values are ignored. */
  private spawnUpgrade(x: number, y: number, key: string) {
    const u = UPGRADES.find((up) => up.key === key);
    if (!u) return;
    const pickup = this.physics.add.image(x, y, u.pickup);
    (pickup.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.tweens.add({ targets: pickup, y: y - 5, duration: 800, yoyo: true, repeat: -1 });
    this.physics.add.overlap(this.player, pickup, () => {
      if (!pickup.active || this.equipped.has(u.key)) return;
      pickup.destroy();
      this.equipped.add(u.key);
      this.flashText(`Equipped ${u.key}!`, Palette.dan);
    });
  }

  /** Fallback when the map authors no `upgrade` objects: drop the pickups in a
   *  row near Dan's spawn (original behavior). */
  private spawnUpgradePickups(spawn: { x: number; y: number }) {
    UPGRADES.forEach((u, i) => this.spawnUpgrade(spawn.x + 40 + i * 34, spawn.y - 6, u.key));
  }

  /** Place each equipped accessory on the current animation frame (position,
   *  rotation, scale, stacking), mirroring when Dan faces left. */
  private updateAccessories() {
    const left = this.facing < 0;
    for (const u of UPGRADES) {
      const spr = this.accSprites[u.key];
      const t = this.equipped.has(u.key) ? accessoryTransform(this.player, u.key) : null;
      if (!t) {
        spr.setVisible(false);
        continue;
      }
      // Offsets and scale are authored against Dan at scale 1, so multiply both by
      // DAN_SCALE to keep the accessory sized and seated correctly as Dan scales.
      spr
        .setVisible(true)
        .setPosition(this.player.x + this.facing * t.ox * DAN_SCALE, this.player.y + t.oy * DAN_SCALE)
        .setFlipX(left)
        .setRotation((left ? -t.rot : t.rot) * (Math.PI / 180))
        .setScale(t.sx * DAN_SCALE, t.sy * DAN_SCALE)
        .setDepth(this.player.depth + (t.front ? 1 : -1));
    }
  }

  override update() {
    if (this.finished) return;
    const body = this.player.body as Phaser.Physics.Arcade.Body;

    // While the PE Guy interstitial is open, Dan stands still and input is ignored.
    if (this.peTalking) {
      this.player.setVelocityX(0);
      updateCharacterAnim(this.player, 0);
      return;
    }

    // Elevators go UP only: they sit still until Dan steps on, then climb to the top
    // and park there for good (no return trip). His upward motion is carried by the
    // platform pushing into him.
    for (const e of this.elevators) {
      if (e.done) {
        e.sprite.setVelocityY(0);
        continue;
      }
      const eb = e.sprite.body as Phaser.Physics.Arcade.Body;
      if (!e.rising) {
        const onIt =
          body.blocked.down &&
          Math.abs(body.bottom - eb.top) < 6 &&
          this.player.x > eb.left &&
          this.player.x < eb.right;
        if (onIt) e.rising = true;
        else {
          e.sprite.setVelocityY(0);
          continue;
        }
      }
      if (e.sprite.y <= e.topY) {
        e.sprite.y = e.topY;
        e.sprite.setVelocityY(0);
        e.done = true;
      } else {
        e.sprite.setVelocityY(-e.speed);
      }
    }

    if (this.leftDown()) {
      this.player.setVelocityX(-RUN_SPEED);
      this.facing = -1;
    } else if (this.rightDown()) {
      this.player.setVelocityX(RUN_SPEED);
      this.facing = 1;
    } else {
      this.player.setVelocityX(0);
    }

    if (this.jumpJustDown() && body.blocked.down) this.player.setVelocityY(-JUMP_SPEED);
    if (this.throwJustDown()) this.startThrow();

    updateCharacterAnim(this.player, this.throwUntil);
  }
}
